import React, { Dispatch, ReactNode, useEffect, useState } from "react"
import api from "../api2"
import RefreshModal from "../components/Admin/RefreshModal"
import { CampingStyleProps, CampProps, ReservationProps, SiteProps, UserProps } from "../interfaces"
import { AdminContext, ICtxPayload } from "./AdminContext"
import IGroupedReservation from "../interfaces/IGroupedReservation"
import MessageModal from "../components/Admin/Reservations/MessageModal"
import Notification, { NotificationContainer } from "../components/ui/Notification"
import * as Sentry from "@sentry/react"
import Modal from "../components/ui/Modal"
import ConfirmationModalContent, {
  IUpdatingReservation,
} from "../components/Admin/Reservations/ConfirmationModalContent"
import { IReservationState } from "~/interfaces/IReservationStates"
import eSendingMode from "../enums/eSendingMode"
import ICamper from "../interfaces/ICamper"
import PosModal from "../components/Reservations/PosModal"
import { useConfirm } from "~/hooks/useConfirm"
import IInvoice from "../interfaces/IInvoice"
import ICampLite from "~/interfaces/ICampLite"
import { adminPages } from "~/types/adminPages"
import eAdminUserRole from "~/enums/eAdminUserRole"
import { useOrderInvoice } from "~/components/Admin/Reservations/ReservationHooks/useOrderInvoice"
import HelpPopover from "~/components/Admin/HelpPopover"
import { reservationStatus } from "~/constants"
import Spinner from "~/components/ui/Spinner"
import { useCamp } from "~/api2/hooks/useCamp"

interface ComponentProps {
  currentPage: adminPages
  setCurrentPage: Dispatch<React.SetStateAction<adminPages>>
  currentUserEmail: string
  currentUserId: number
  currentUserRole: eAdminUserRole
  camp: CampProps
  setCamp: Dispatch<React.SetStateAction<CampProps>>
  sites: SiteProps[]
  campingStyles: CampingStyleProps[]
  setCampingStyles: Dispatch<React.SetStateAction<CampingStyleProps[]>>
  setSites: Dispatch<React.SetStateAction<SiteProps[]>>
  selectedReservation: ReservationProps
  setSelectedReservation: Dispatch<React.SetStateAction<ReservationProps>>
  children?: React.ReactNode
}

type ModalState = {
  id?: string
  content: ReactNode
  isDynamicWidth: boolean
  maxWidth?: string
  closeCallback?: () => any
  width?: string
}

export const AdminProvider = ({
  currentPage,
  setCurrentPage,
  currentUserEmail,
  currentUserId,
  currentUserRole,
  camp,
  setCamp,
  sites,
  campingStyles,
  setCampingStyles,
  setSites,
  selectedReservation,
  setSelectedReservation,
  children,
}: ComponentProps) => {
  const { showConfirm, ConfirmModal } = useConfirm()

  const [reservationStates, setReservationStates] = useState<Record<string, IReservationState>>({})
  const [refreshModalOpen, setRefreshModalOpen] = useState(false)
  const [isShowingPaymentModal, setIsShowingPaymentModal] = useState(false)
  const [paymentModalAmountInCents, setPaymentModalAmountInCents] = useState(0)
  const [paymentModalReservation, setPaymentModalReservation] = useState<ReservationProps>()
  const [paymentModalReservations, setPaymentModalReservations] = useState<ReservationProps[]>()
  const [paymentModalGroupedReservation, setPaymentModalGroupedReservation] = useState<IGroupedReservation>()
  const [paymnentModalIsDeposit, setPaymentModalIsDeposit] = useState<boolean | undefined>(false)
  const [paymentModalCloseCallback, setPaymentModalCloseCallback] = useState<() => any>()
  const [isShowingMessageModal, setIsShowingMessageModal] = useState(false)
  const [messageModalReservations, setMessageModalReservations] = useState<ReservationProps[]>()
  const [messageModalInvoices, setMessageModalInvoices] = useState<IInvoice[]>()
  const [messageModalEmails, setMessageModalEmails] = useState<string[]>()
  const [messageModalPhoneNumbers, setMessageModalPhoneNumbers] = useState<string[]>()
  const [isMessageModalBatch, setIsMessageModalBatch] = useState(false)
  const [isNotification, setIsNotification] = useState(false)
  const [notificationType, setNotificationType] = useState<"success" | "warning" | "error">("success")
  const [notificationTitle, setNotificationTitle] = useState("")
  const [notificationDescription, setNotificationDescription] = useState<string>("")
  const [notificationDismissAfter, setNotificationDismissAfter] = useState<number>()
  const [showTimer, setShowTimer] = useState<boolean>(false)
  const [notificationOnClick, setNotificationOnClick] = useState<(e: { event: React.MouseEvent }) => void>()
  const [isMessageSent, setIsMessageSent] = useState(false)
  const [selectedSendingMode, setSelectedSendingMode] = useState<eSendingMode>(eSendingMode.EMAIL)
  const [accessibleCamps, setAccessibleCamps] = useState<ICampLite[]>([])
  const [modals, setModals] = useState<ModalState[]>([])
  const [selectedCamper, setSelectedCamper] = useState<ICamper | null>(null)
  const [isCCBlocked, setisCCBlocked] = useState(false)
  const [isAttachmentsDisabled, setIsAttachmentsDisabled] = useState(false)
  const orderInvoice = useOrderInvoice({ orderId: selectedReservation?.order_id })
  const [isSlideoverOpen, setIsSlideoverOpen] = useState(false)
  const isParkAdminUser = currentUserEmail?.includes("@poweredbypark.com") || false
  const [initialLoadDone, setInitialLoadDone] = useState(false)
  const {
    index: { data: queryCamp, isLoading: isLoadingCamp },
  } = useCamp(camp.id)

  useEffect(() => {
    if (!isLoadingCamp && queryCamp && !initialLoadDone) {
      setInitialLoadDone(true)
    }
  }, [isLoadingCamp, queryCamp, initialLoadDone])

  const showLoader = !initialLoadDone && (isLoadingCamp || !queryCamp)

  const reloadAccessibleCamps = async () => {
    return api.Me.camps()
      .then(({ camps }) => {
        setAccessibleCamps(camps)
      })
      .catch((err) => {
        console.error(err)
        Sentry.captureMessage(`Error fetching camps for admin user - ${err.message}`)
      })
  }

  const showModal = (
    component: ReactNode,
    isDynamicWidth = true,
    maxWidth?: string,
    options: { id?: string; closeCallback?: () => any } = {},
    width?: string,
  ) => {
    setModals((prev) => [
      ...prev,
      {
        id: options.id,
        content: component,
        isDynamicWidth,
        maxWidth: maxWidth || "max-w-6xl",
        closeCallback: options.closeCallback,
        width,
      },
    ])
  }

  // Updated closeModal that optionally takes an id. If no id is passed, all modals are closed.
  const closeModal = (id?: string) => {
    if (id) {
      setModals((prev) => prev.filter((modal) => modal.id !== id))
    } else {
      setModals([])
    }
  }

  const showPaymentModal = (
    amountInCents: number,
    options: {
      reservation?: ReservationProps
      reservations?: ReservationProps[]
      groupedReservation?: IGroupedReservation
      isDeposit?: boolean
      closeCallback?: () => any
    } = {},
  ) => {
    setPaymentModalAmountInCents(amountInCents)
    setPaymentModalReservation(options.reservation)
    setPaymentModalReservations(options.reservations)
    setPaymentModalGroupedReservation(options.groupedReservation)
    setPaymentModalIsDeposit(options.isDeposit)
    if (options.closeCallback) {
      setPaymentModalCloseCallback(() => options.closeCallback)
    } else {
      setPaymentModalCloseCallback(undefined)
    }
    setIsShowingPaymentModal(true)
  }

  const handleClosePaymentModal = () => {
    setIsShowingPaymentModal(false)
    if (paymentModalCloseCallback) paymentModalCloseCallback()
  }

  const showMessageModal = ({
    reservations,
    invoices,
    emails,
    phoneNumbers,
    isBatch,
    isCCBlocked,
    isAttachmentsDisabled,
  }: Parameters<ICtxPayload["showMessageModal"]>[0]) => {
    setMessageModalReservations(reservations)
    setMessageModalInvoices(invoices)
    setMessageModalEmails(emails)
    setMessageModalPhoneNumbers(phoneNumbers)
    setIsMessageModalBatch(!!isBatch)
    setisCCBlocked(!!isCCBlocked)
    setIsShowingMessageModal(true)
    setIsAttachmentsDisabled(!!isAttachmentsDisabled)
  }

  const showRefreshModal = () => {
    setRefreshModalOpen(true)
  }

  /** @deprecated */
  const updateLocalReservation = (res: ReservationProps) => {
    const mReservations = { ...reservationStates }
    const reservationId: string = res.id as string

    if (res.deleted_at) {
      delete mReservations[reservationId]
    } else {
      mReservations[reservationId] = {
        persisted: { ...res },
        updated: undefined,
      }
    }

    setReservationStates(mReservations)
  }

  const updateLocalReservations = (rs: ReservationProps[]) => {
    const mReservations = { ...reservationStates }

    rs.forEach((res) => {
      const reservationId: string = res.id as string
      if (res.deleted_at) {
        delete mReservations[reservationId]
      } else {
        mReservations[reservationId] = {
          persisted: { ...res },
          updated: undefined,
        }
      }
    })
    setReservationStates(mReservations)
  }

  const updateTodoReservations = (reservation: { id?: string; status?: reservationStatus }) => {
    const mReservations = { ...reservationStates }
    if (reservation.id) {
      mReservations[reservation.id] = {
        persisted: mReservations[reservation.id].persisted,
        updated: { ...mReservations[reservation.id], status: reservation.status },
      }
      setReservationStates(mReservations)
    }
  }

  const showNotification = (args: Parameters<ICtxPayload["showNotification"]>[0]) => {
    setNotificationType(args.type)
    setNotificationTitle(args.title)
    setNotificationDescription(args.description as string)
    setNotificationDismissAfter(args.dismissAfter || 5000)
    setIsNotification(true)
    setNotificationOnClick(args.onClick)
    setShowTimer(args.showTimer || false)
  }

  const showReservationsConfirmation = (
    reservationStates: IReservationState[],
    customCallback?: (data: IUpdatingReservation[]) => Promise<any>,
  ) => {
    showModal(
      <ConfirmationModalContent reservationStates={reservationStates} customCallback={customCallback} />,
      true,
      "max-w-6xl",
    )
  }

  if (showLoader) {
    return (
      <div className="w-full h-full flex justify-center items-center">
        <Spinner size={5} />
        <div className="">Loading your camp...</div>
      </div>
    )
  }

  return (
    <AdminContext.Provider
      value={{
        orderInvoice,
        currentPage,
        setCurrentPage,
        currentUserEmail,
        currentUserId,
        currentUserRole,
        isParkAdminUser,
        camp,
        setCamp,
        accessibleCamps,
        reloadAccessibleCamps,
        sites,
        setSites,
        campingStyles,
        setCampingStyles,
        reservationStates,
        setReservationStates,
        selectedReservation,
        setSelectedReservation,
        selectedCamper,
        setSelectedCamper,
        showRefreshModal,
        showPaymentModal,
        showMessageModal,
        showModal,
        closeModal,
        setIsMessageSent,
        selectedSendingMode,
        setSelectedSendingMode,
        showNotification,
        updateLocalReservation,
        updateLocalReservations,
        showReservationsConfirmation,
        showConfirm,
        isSlideoverOpen,
        setIsSlideoverOpen,
        modalsLength: modals.length,
      }}
    >
      <RefreshModal open={refreshModalOpen} onClose={() => setRefreshModalOpen(false)} />
      {modals.map((modal, index) => (
        <Modal
          key={index}
          open={true}
          onClose={() => {
            closeModal(modal.id)
            if (modal.closeCallback && !modal.id) modal.closeCallback()
          }}
          maxWidth={modal.maxWidth}
          width={modal.width}
          isDynamicWidth={modal.isDynamicWidth}
        >
          {modal.content}
        </Modal>
      ))}
      {isShowingPaymentModal && (
        <PosModal
          open={isShowingPaymentModal}
          setOpen={setIsShowingPaymentModal}
          onClose={handleClosePaymentModal}
          owedInCents={paymentModalAmountInCents}
          reservation={paymentModalReservation}
          reservations={paymentModalReservations}
          groupedReservation={paymentModalGroupedReservation}
          isDeposit={paymnentModalIsDeposit}
          isInitializedDeposit={paymnentModalIsDeposit}
          // TODO: drop this when we're all in on orders
          onChangeIntentId={async (intentId) => {
            if (!intentId) return

            if (!paymentModalGroupedReservation) {
              const { reservation: updatedRes } = await api.Reservations.update(paymentModalReservation?.id as string, {
                vendor_id: intentId,
                is_manual_charge: true,
              })
              updateLocalReservations([updatedRes])
            }
          }}
          updateReservationCallback={(reservation) => updateTodoReservations(reservation)}
        />
      )}
      <MessageModal
        open={isShowingMessageModal}
        onClose={() => {
          setMessageModalEmails([])
          setMessageModalReservations([])
          setIsShowingMessageModal(false)
          setSelectedReservation(null)
        }}
        reservations={messageModalReservations}
        invoices={messageModalInvoices}
        emails={messageModalEmails}
        phoneNumbers={messageModalPhoneNumbers}
        isBatch={isMessageModalBatch}
        isCCBlocked={isCCBlocked}
        isAttachmentsDisabled={isAttachmentsDisabled}
      />
      <ConfirmModal />
      <NotificationContainer>
        <Notification
          visible={isMessageSent}
          success
          title="Message sent!"
          description="Your message was successfully sent."
          onDismiss={() => setIsMessageSent(false)}
          showTimer={showTimer}
        />
        <Notification
          visible={isNotification}
          success={notificationType === "success"}
          warning={notificationType === "warning"}
          error={notificationType === "error"}
          title={notificationTitle}
          description={notificationDescription}
          onDismiss={() => setIsNotification(false)}
          dismissAfter={notificationDismissAfter}
          onClick={notificationOnClick}
          showTimer={showTimer}
        />
      </NotificationContainer>
      <HelpPopover currentPage={currentPage} />

      {children}
    </AdminContext.Provider>
  )
}
